import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { WalletProvider } from "@solana/wallet-adapter-react";
import {
  PhantomWalletAdapter,
  SafePalWalletAdapter,
  SolflareWalletAdapter,
  TrustWalletAdapter,
  WalletConnectWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import { CoinbaseWalletAdapter } from "@solana/wallet-adapter-wallets";
import { coinbaseWallet, safe, walletConnect } from "@wagmi/connectors";
// import { walletConnect } from "@wagmi/connectors";
import { createConfig, createStorage, http, injected } from "@wagmi/core";
import { cookieStorage } from "@wagmi/core";
import { NetworkSymbols } from "based-auth/networks";
import { createContext, useContext, useMemo, useRef, useState } from "react";

interface SimpleKitContextType {
  chains: any[];
  wagmiConfig: any;
  signMessage: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pendingItem: any;
  setPendingItem: React.Dispatch<React.SetStateAction<any>>;
  router: any;
  setRouter: React.Dispatch<React.SetStateAction<any>>;
}

export const titles = {
  home: "Connect",
  account: "Account",
  walletConnecting: "Wallet Connecting",
  selectETHWallet: "Select ETH Wallet",
  selectSolanaWallet: "Select Solana Wallet",
  walletqr: "Wallet QR",
  switchAccount: "Switch Account",
  switchingAccount: "Switching Account",
  switchChain: "Switch Chain",
  switchingChain: "Switching Chain",
  withdrawFunds: "Withdraw Funds",
  withdrawingFunds: "Withdrawing Funds",
  accountSigning: "Account Signing",
};

export const routes = {
  home: "home",
  account: "account",
  accountSigning: "accountSigning",
  socialConnecting: "socialConnecting",
  walletConnecting: "walletConnecting",
  selectETHWallet: "selectETHWallet",
  selectSolanaWallet: "selectSolanaWallet",
  walletqr: "walletqr",
  switchAccount: "switchAccount",
  switchingAccount: "switchingAccount",
  switchChain: "switchChain",
  switchingChain: "switchingChain",
  withdrawFunds: "withdrawFunds",
  withdrawingFunds: "withdrawingFunds",
};

export const SimpleKitContext = createContext<SimpleKitContextType>({
  chains: [],
  wagmiConfig: null,
  signMessage: "based.bid welcomes you!!!",
  pendingItem: null,
  setPendingItem: () => {},
  open: false,
  setOpen: () => {},
  router: routes.home,
  setRouter: () => {},
});

export const useSimpleKitContext = () => {
  return useContext(SimpleKitContext);
};

export const useSimpleKit = () => {
  const { open, setOpen, ...rest } = useContext(SimpleKitContext);
  return {
    isOpen: open,
    openModal: () => {
      setOpen(true);
    },
    closeModal: () => {
      setOpen(false);
    },
    ...rest,
  };
};

export const SimpleKitContextProvider = ({
  children,
  chains,
  signMessage,
  metadata,
  projectId,
}: {
  children: React.ReactNode;
  chains: any[];
  signMessage: string;
  metadata: any;
  projectId: string;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [router, setRouter] = useState<any>(null);
  const [pendingItem, setPendingItem] = useState<any>(null);
  const ethWConnector = useRef<any>(null);
  const solWConnector = useRef<any>(null);

  if (!ethWConnector.current) {
    ethWConnector.current = walletConnect({
      projectId,
      metadata,
    });
  }

  if (!solWConnector.current) {
    solWConnector.current = new WalletConnectWalletAdapter({
      network: WalletAdapterNetwork.Mainnet,
      options: {
        projectId,
        metadata,
      },
    });
  }

  const wagmiConfig = useMemo(() => {
    const filteredChains = chains.filter(
      (item) => item.network.symbol === NetworkSymbols.eth
    ) as any;
    const isInIframe =
      !(typeof window === "undefined") && window?.parent !== window;
    return createConfig({
      chains: filteredChains,
      connectors: [
        injected({ target: "metaMask" }),
        ethWConnector.current,
        coinbaseWallet({
          appName: metadata.name,
          appLogoUrl: metadata.icons[0],
          overrideIsMetaMask: false,
          preference: "all",
        }),
        ...(isInIframe
          ? [
              safe({
                allowedDomains: [/gnosis-safe.io$/, /app.safe.global$/],
              }),
            ]
          : []),
      ],
      storage: createStorage({
        storage: cookieStorage,
      }),
      transports: Object.fromEntries(
        filteredChains.map((chain: any) => [chain.id, http()])
      ),
    });
  }, [chains]);

  const solWallets = useMemo(
    () => [
      new CoinbaseWalletAdapter(),
      new PhantomWalletAdapter(),
      new SafePalWalletAdapter(),
      new SolflareWalletAdapter(),
      new TrustWalletAdapter(),
      solWConnector.current,
    ],
    []
  );

  return (
    <SimpleKitContext.Provider
      value={{
        chains,
        wagmiConfig,
        signMessage,
        open,
        setOpen,
        router,
        setRouter,
        pendingItem,
        setPendingItem,
      }}
    >
      <WalletProvider wallets={solWallets} autoConnect>
        {children}
      </WalletProvider>
    </SimpleKitContext.Provider>
  );
};
